/**
 * Grid helpers shared by views_dashboard, views_grid, views_bench, views_budget.
 * Verbatim port of helpers defined in designv2/src/views_grid.jsx (windowMonths +
 * useScrollCurrentIntoView). Extracted here so view files can be ported
 * independently — they all import from this single helper module.
 */
import { useLayoutEffect } from "react";

import { type MonthEntry, monthsBetween } from "../tokens";

export function windowMonths(rangeStart: string, rangeEnd: string): MonthEntry[] {
  return monthsBetween(rangeStart, rangeEnd);
}

/**
 * Scrolls elements flagged `data-current-month="true"` into the horizontal
 * center of their nearest overflow:auto ancestor. No-op when the current
 * month isn't in range. Verbatim port from designv2/src/views_grid.jsx.
 */
export function useScrollCurrentIntoView(
  rangeStart: string,
  rangeEnd: string,
  viewKey: string,
): void {
  useLayoutEffect(() => {
    // Deliberate dep references — the effect re-fires when range/view changes
    // so the scroll-into-view runs against the new layout, even though the
    // values aren't read inside the body.
    void rangeStart;
    void rangeEnd;
    void viewKey;
    let cancelled = false;
    const observers: ResizeObserver[] = [];
    const timers: ReturnType<typeof setTimeout>[] = [];
    function findContainer(target: Element): HTMLElement | null {
      let c = target.parentElement;
      while (c && c !== document.body) {
        const ov = getComputedStyle(c).overflowX;
        if (ov === "auto" || ov === "scroll") return c;
        c = c.parentElement;
      }
      return null;
    }
    function scrollOne(target: Element): boolean {
      const container = findContainer(target);
      if (!container) return false;
      if (container.scrollWidth <= container.clientWidth + 2) return true;
      const cRect = container.getBoundingClientRect();
      const tRect = target.getBoundingClientRect();
      const want = Math.max(
        0,
        tRect.left - cRect.left + container.scrollLeft - cRect.width / 2 + tRect.width / 2,
      );
      const maxLeft = container.scrollWidth - container.clientWidth;
      const final = Math.min(want, maxLeft);
      container.scrollLeft = final;
      return Math.abs(container.scrollLeft - final) <= 2;
    }
    function pass(): boolean {
      if (cancelled) return false;
      const targets = document.querySelectorAll('[data-current-month="true"]');
      if (!targets.length) return false;
      let allOk = true;
      for (const t of Array.from(targets)) {
        if (!scrollOne(t)) allOk = false;
      }
      return allOk;
    }
    const delays = [0, 30, 90, 180, 320, 520, 800, 1200];
    for (const d of delays) {
      timers.push(setTimeout(() => pass(), d));
    }
    requestAnimationFrame(() => {
      if (cancelled) return;
      for (const t of Array.from(document.querySelectorAll('[data-current-month="true"]'))) {
        const c = findContainer(t);
        if (!c) continue;
        const ro = new ResizeObserver(() => {
          pass();
        });
        ro.observe(c);
        observers.push(ro);
      }
    });
    return () => {
      cancelled = true;
      for (const t of timers) clearTimeout(t);
      for (const o of observers) o.disconnect();
    };
  }, [rangeStart, rangeEnd, viewKey]);
}

/** "2026-03" → "Mar 2026" — used in edit-lock subtitles. */
export function prevMonthLabel(k: string | null | undefined): string {
  if (!k) return "";
  const [y, m] = k.split("-").map(Number);
  const names = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  return `${names[(m ?? 1) - 1]} ${y}`;
}
